<template>
  <div class="cal-month">
    <div
      class="cal-month__weekdays"
      v-bind="passThrough('weekdayHeader')"
    >
      <div
        v-for="name in weekdayNames"
        :key="name"
        class="cal-month__weekday"
      >
        {{ name }}
      </div>
    </div>

    <div
      ref="gridEl"
      class="cal-month__grid"
      :style="{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }"
    >
      <template
        v-for="(week, weekIndex) in weeks"
        :key="weekIndex"
      >
        <div
          v-for="cell in week"
          :key="cell.key"
          class="cal-month__cell"
          :class="{
            'cal-month__cell--outside': !cell.isCurrentMonth,
            'cal-month__cell--today': cell.isToday,
            'cal-month__cell--weekend': cell.isWeekend,
            'cal-month__cell--blocked': cellFor(cell.key).blockouts.length > 0,
            'cal-month__cell--full': cellFor(cell.key).isFull,
            'cal-month__cell--drop-target': dropTargetDate === cell.key,
            'cal-month__cell--drop-invalid': dropTargetDate === cell.key && !isDropValid
          }"
          data-calendar-drop="day"
          :data-date="cell.key"
          v-bind="passThrough('dayCell')"
          @click="onCellClick(cell, $event)"
        >
          <div class="cal-month__cell-header">
            <slot
              name="day-cell-header"
              :cell="cell"
              :events="cellFor(cell.key).events"
            >
              <span
                class="cal-month__day-number"
                v-bind="passThrough('dayNumber')"
              >
                {{ cell.dayNumber }}
              </span>
              <span
                v-if="compact && cellFor(cell.key).events.length > 0"
                class="cal-month__count"
              >
                {{ cellFor(cell.key).events.length }}
              </span>
            </slot>
          </div>

          <!-- Blockout labels sit above events so a closed day reads at a glance -->
          <div
            v-for="blockout in cellFor(cell.key).blockouts"
            :key="blockout.id"
            class="cal-month__blockout"
            :style="{ '--cal-blockout-accent': resolveCalendarColor(blockout.color, DEFAULT_BLOCKOUT_COLOR) }"
            v-bind="passThrough('blockout')"
          >
            <slot
              name="blockout"
              :blockout="blockout"
            >
              <Icon
                v-if="blockout.icon"
                :type="blockout.icon"
                class="cal-month__blockout-icon"
              />
              <span class="cal-month__blockout-title">{{ blockout.title }}</span>
            </slot>
          </div>

          <!-- Compact cells replace chips with coloured dots to stay legible on phones -->
          <div
            v-if="compact"
            class="cal-month__dots"
          >
            <span
              v-for="event in cellFor(cell.key).dots"
              :key="event.id"
              class="cal-month__dot"
              :style="{ backgroundColor: resolveCalendarColor(event.color) }"
            />
          </div>

          <div
            v-else
            class="cal-month__events"
          >
            <CalendarEvent
              v-for="event in cellFor(cell.key).layout.visible"
              :key="event.id"
              :event="event"
              variant="dot"
              :hour12="hour12"
              :timezone="timezone"
              :is-dragging="event.id === draggingEventId"
              :is-draggable="isEventDraggable(event)"
              @pointerdown="emit('event-pointerdown', $event, event)"
              @pointerenter="emit('event-hover', $event, event)"
              @pointerleave="emit('event-leave')"
              @click="onEventClick(event, $event)"
              @dblclick="onEventDblClick(event, $event)"
            >
              <template
                v-if="$slots.event"
                #default
              >
                <slot
                  name="event"
                  :event="event"
                />
              </template>
            </CalendarEvent>

            <button
              v-if="cellFor(cell.key).layout.overflowCount > 0"
              type="button"
              class="cal-month__more"
              v-bind="passThrough('moreLink')"
              @click.stop="onMoreClick(cell.key, $event)"
            >
              <slot
                name="more-link"
                :count="cellFor(cell.key).layout.overflowCount"
              >
                +{{ cellFor(cell.key).layout.overflowCount }} {{ $t('ui.common.more').toLowerCase() }}
              </slot>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'
import type { Dayjs } from 'dayjs'
import { computed, nextTick, ref, watch } from 'vue'

// components
import CalendarEvent from '@/components/calendar/CalendarEvent.vue'
import Icon from '@/components/icon/Icon.vue'

// types
import type {
  CalendarBlockout,
  CalendarCapacityRule,
  CalendarDayCell,
  CalendarEvent as CalendarEventType,
  CalendarMonthCellLayout
} from '@/types/components/calendar'

// utilities
import { DEFAULT_BLOCKOUT_COLOR, resolveCalendarColor } from '@/utilities/calendar/color'
import { getBlockoutsForDay, getCapacityUsage } from '@/utilities/calendar/constraints'
import { getEventsForDay } from '@/utilities/calendar/datetime'
import { countMonthCellSlots, layoutMonthCell } from '@/utilities/calendar/layout'
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'

/** Everything a single cell needs, computed once per render pass */
interface MonthCellData {
  events: CalendarEventType[]
  layout: CalendarMonthCellLayout
  blockouts: CalendarBlockout[]
  dots: CalendarEventType[]
  isFull: boolean
}

/** How many dots a compact cell shows before it stops adding more */
const MAX_COMPACT_DOTS = 4

const props = withDefaults(
  defineProps<{
    /** Month grid rows */
    weeks: CalendarDayCell[][]
    /** Weekday header names */
    weekdayNames: string[]
    /** Events to place */
    events: CalendarEventType[]
    /** Blockouts to place */
    blockouts: CalendarBlockout[]
    /** Capacity rules used to mark full days */
    capacityRules: CalendarCapacityRule[]
    /** Chips shown per cell before overflow; 0 means fit as many as the cell can hold */
    maxEventsPerCell: number
    /** Renders dots and counts instead of chips, for narrow screens */
    compact?: boolean
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Calendar-level fallback timezone */
    timezone?: string
    /** Id of the event currently being dragged */
    draggingEventId?: string | null
    /** Date beneath the drag pointer */
    dropTargetDate?: string | null
    /** Whether the current drop target accepts the drag */
    isDropValid?: boolean
    /** Decides whether an event may be dragged */
    isEventDraggable: (event: CalendarEventType) => boolean
  }>(),
  {
    compact: false,
    hour12: false,
    timezone: undefined,
    draggingEventId: null,
    dropTargetDate: null,
    isDropValid: true
  }
)

const emit = defineEmits<{
  'date-click': [date: Dayjs]
  'event-click': [event: CalendarEventType, payload: MouseEvent | KeyboardEvent]
  'event-dblclick': [event: CalendarEventType, payload: MouseEvent]
  'event-pointerdown': [payload: PointerEvent, event: CalendarEventType]
  'event-hover': [payload: PointerEvent, event: CalendarEventType]
  'event-leave': []
  'overflow-click': [date: string, events: CalendarEventType[], payload: MouseEvent]
}>()

const passThrough = useCalendarPassThrough()

/** Month grid element, observed so overflow counts track the live cell height */
const gridEl = ref<HTMLElement | null>(null)

/**
 * Measured chip metrics for the current grid.
 * Fallbacks match the default month-cell CSS so the first paint is close.
 */
const slotMetrics = ref({
  eventsAreaHeight: 0,
  eventHeight: 18,
  moreHeight: 15,
  blockoutHeight: 16,
  gap: 1,
  cellGap: 1
})

/**
 * Reads the used height of an element, preferring subpixel boxes so the last chip is not dropped.
 * @param el - Element to measure
 */
function readHeight(el: Element | null): number {
  // Missing nodes fall back to the CSS defaults at the call site
  if (!el) {
    return 0
  }

  return el.getBoundingClientRect().height
}

/**
 * Whether two metric snapshots are close enough that a reactive write would only loop.
 * @param left - Previously stored metrics
 * @param right - Newly measured metrics
 */
function slotMetricsUnchanged(left: typeof slotMetrics.value, right: typeof slotMetrics.value): boolean {
  return (
    Math.abs(left.eventsAreaHeight - right.eventsAreaHeight) < 0.5 &&
    Math.abs(left.eventHeight - right.eventHeight) < 0.5 &&
    Math.abs(left.moreHeight - right.moreHeight) < 0.5 &&
    Math.abs(left.blockoutHeight - right.blockoutHeight) < 0.5 &&
    Math.abs(left.gap - right.gap) < 0.5 &&
    Math.abs(left.cellGap - right.cellGap) < 0.5
  )
}

/**
 * Reads chip and cell sizes from the live DOM so overflow counts stay accurate
 * as the calendar is resized or the week count changes.
 */
function updateSlotMetrics(): void {
  const cell = gridEl.value?.querySelector('.cal-month__cell') as HTMLElement | null

  // The grid has not rendered yet
  if (!cell) {
    return
  }

  const header = cell.querySelector('.cal-month__cell-header') as HTMLElement | null
  const eventsEl = cell.querySelector('.cal-month__events') as HTMLElement | null
  const eventEl = gridEl.value?.querySelector('.cal-month__events .cal-event') as HTMLElement | null
  const moreEl = gridEl.value?.querySelector('.cal-month__more') as HTMLElement | null
  const blockoutEl = gridEl.value?.querySelector('.cal-month__blockout') as HTMLElement | null
  const cellStyles = getComputedStyle(cell)
  const cellGap = Number.parseFloat(cellStyles.rowGap || cellStyles.gap) || 2
  const paddingBottom = Number.parseFloat(cellStyles.paddingBottom) || 0
  const borderBottom = Number.parseFloat(cellStyles.borderBottomWidth) || 0
  const cellRect = cell.getBoundingClientRect()
  const headerRect = header?.getBoundingClientRect()
  const innerBottom = cellRect.bottom - borderBottom - paddingBottom
  // Space below the date number, after the cell's flex gap, is what chips can actually use
  const belowHeader = headerRect ? Math.max(0, innerBottom - headerRect.bottom - cellGap) : 0
  const eventsArea = eventsEl ? readHeight(eventsEl) : 0

  const next = {
    // A flexed events area is the source of truth; geometry covers the first paint before it stretches
    eventsAreaHeight: Math.max(eventsArea, belowHeader),
    eventHeight: readHeight(eventEl) || 20,
    moreHeight: readHeight(moreEl) || 16,
    blockoutHeight: readHeight(blockoutEl) || 16,
    gap: eventsEl ? Number.parseFloat(getComputedStyle(eventsEl).gap) || 1 : 1,
    cellGap
  }

  // Skip the write when nothing moved, otherwise measuring after render loops
  if (slotMetricsUnchanged(slotMetrics.value, next)) {
    return
  }

  slotMetrics.value = next

  // Chip heights are only known after the new visible count paints
  void nextTick(() => {
    updateSlotMetrics()
  })
}

useResizeObserver(gridEl, updateSlotMetrics)

// Events often arrive after the grid is sized; remeasure so fallback chip heights are replaced
watch(
  () => [props.events, props.weeks, props.compact],
  () => {
    void nextTick(() => {
      updateSlotMetrics()
    })
  }
)

/**
 * How many chips a cell can show given its measured height and blockouts.
 * @param eventCount - Events that want to render in the cell
 * @param blockoutCount - Blockout rows already occupying the cell
 */
function maxVisibleForCell(eventCount: number, blockoutCount: number): number {
  const metrics = slotMetrics.value

  // Until the grid has a real height, keep a conservative guess so chips do not flash in
  if (metrics.eventsAreaHeight <= 0) {
    return props.maxEventsPerCell > 0 ? props.maxEventsPerCell : 3
  }

  const blockoutStack = blockoutCount > 0 ? blockoutCount * metrics.blockoutHeight + blockoutCount * metrics.cellGap : 0
  const available = Math.max(0, metrics.eventsAreaHeight - blockoutStack)
  const fitted = countMonthCellSlots(available, eventCount, {
    eventHeight: metrics.eventHeight,
    moreHeight: metrics.moreHeight,
    gap: metrics.gap
  })

  // A positive prop is a hard cap on top of the measured fit
  if (props.maxEventsPerCell > 0) {
    return Math.min(props.maxEventsPerCell, fitted)
  }

  return fitted
}

/**
 * Per-cell data keyed by 'YYYY-MM-DD'.
 * Built in one pass so each cell does not re-scan the full event list while rendering.
 */
const cellData = computed(() => {
  const map = new Map<string, MonthCellData>()

  props.weeks.flat().forEach(cell => {
    const events = getEventsForDay(props.events, cell.date, props.timezone)
    const blockouts = getBlockoutsForDay(cell.date, props.blockouts, props.timezone)
    const dayStart = cell.date.startOf('day')
    const dayEnd = dayStart.add(1, 'day')

    // A day is "full" when any capacity rule covering it has been reached
    const isFull = props.capacityRules.some(rule => {
      const usage = getCapacityUsage(rule, props.events, { timezone: props.timezone })

      return usage.isFull && dayStart.isBefore(rule.end) && dayEnd.isAfter(rule.start)
    })

    map.set(cell.key, {
      events,
      layout: layoutMonthCell(events, maxVisibleForCell(events.length, blockouts.length), props.timezone),
      blockouts,
      dots: events.slice(0, MAX_COMPACT_DOTS),
      isFull
    })
  })

  return map
})

/** Empty cell data reused whenever a lookup misses, so the template never guards for null */
const EMPTY_CELL: MonthCellData = {
  events: [],
  layout: { visible: [], overflow: [], overflowCount: 0 },
  blockouts: [],
  dots: [],
  isFull: false
}

/**
 * Reads a cell's precomputed data.
 * @param key - Cell date as 'YYYY-MM-DD'
 */
function cellFor(key: string): MonthCellData {
  return cellData.value.get(key) ?? EMPTY_CELL
}

/**
 * Emits a date click, ignoring clicks that landed on a chip.
 * @param cell - Cell that was clicked
 * @param mouseEvent - Native click
 */
function onCellClick(cell: CalendarDayCell, mouseEvent: MouseEvent): void {
  const target = mouseEvent.target as HTMLElement

  // Clicks on chips or the overflow link have their own handlers
  if (target.closest('.cal-event') || target.closest('.cal-month__more')) {
    return
  }

  emit('date-click', cell.date)
}

/**
 * Forwards a chip click.
 * @param event - Event that was clicked
 * @param payload - Originating DOM event
 */
function onEventClick(event: CalendarEventType, payload: MouseEvent | KeyboardEvent): void {
  emit('event-click', event, payload)
}

/**
 * Forwards a chip double-click.
 * @param event - Event that was double-clicked
 * @param payload - Originating DOM event
 */
function onEventDblClick(event: CalendarEventType, payload: MouseEvent): void {
  emit('event-dblclick', event, payload)
}

/**
 * Forwards an overflow link click.
 * @param key - Cell date
 * @param mouseEvent - Native click
 */
function onMoreClick(key: string, mouseEvent: MouseEvent): void {
  emit('overflow-click', key, cellFor(key).events, mouseEvent)
}
</script>

<style scoped>
.cal-month {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.cal-month__weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid var(--border-secondary);
}

.cal-month__weekday {
  padding: var(--s-1-5) var(--s-2);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-tertiary);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.cal-month__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  flex: 1;
  min-height: 0;
}

.cal-month__cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  min-height: 0;
  padding: 4px var(--s-2) var(--s-2);
  border-right: 1px solid var(--border-secondary);
  border-bottom: 1px solid var(--border-secondary);
  overflow: hidden;
  transition: background-color 0.12s ease;
}

.cal-month__cell:nth-child(7n) {
  border-right: none;
}

.cal-month__cell--outside {
  background-color: var(--bg-secondary_subtle, var(--bg-secondary));
}

.cal-month__cell--outside .cal-month__day-number {
  color: var(--text-quaternary);
}

/* Diagonal hatching marks a blocked day without hiding its events */
.cal-month__cell--blocked {
  background-image: repeating-linear-gradient(
    -45deg,
    color-mix(in srgb, var(--text-quaternary) 12%, transparent) 0 2px,
    transparent 2px 8px
  );
}

.cal-month__cell--full {
  box-shadow: inset 0 -2px 0 var(--utility-warning-400);
}

.cal-month__cell--drop-target {
  background-color: color-mix(in srgb, var(--bg-brand-solid) 10%, transparent);
  box-shadow: inset 0 0 0 2px var(--border-brand);
}

.cal-month__cell--drop-invalid {
  background-color: color-mix(in srgb, var(--utility-error-500) 10%, transparent);
  box-shadow: inset 0 0 0 2px var(--utility-error-500);
}

.cal-month__cell-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-1);
  padding: 0;
}

.cal-month__day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.2rem;
  height: 1.2rem;
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--text-secondary);
  border-radius: var(--rounded-full);
}

.cal-month__cell--today .cal-month__day-number {
  color: var(--text-white, #fff);
  background-color: var(--bg-brand-solid);
  font-weight: 600;
}

.cal-month__count {
  padding: 0 0.3125rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-tertiary);
  background-color: var(--bg-secondary);
  border-radius: var(--rounded-full);
}

.cal-month__events {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 1px;
  min-height: 0;
  overflow: hidden;
}

.cal-month__events :deep(.cal-event) {
  padding: 0.0625rem 0.25rem;
  line-height: 1.25;
  gap: 0.1875rem;
}

.cal-month__events :deep(.cal-event__dot) {
  width: 0.375rem;
  height: 0.375rem;
}

.cal-month__blockout {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.0625rem 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-tertiary);
  background-color: color-mix(in srgb, var(--cal-blockout-accent) 16%, transparent);
  border-left: 2px solid var(--cal-blockout-accent);
  border-radius: var(--rounded-xs, 2px);
}

.cal-month__blockout-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-month__blockout-icon {
  flex-shrink: 0;
  font-size: 0.625rem;
}

.cal-month__more {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0.0625rem 0.25rem;
  min-height: 0;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.25;
  color: var(--text-tertiary);
  text-align: left;
  background: none;
  border: none;
  border-radius: var(--rounded-sm);
  cursor: pointer;
}

.cal-month__more:hover {
  color: var(--text-secondary);
  background-color: var(--bg-primary_hover);
}

.cal-month__dots {
  display: flex;
  flex-wrap: wrap;
  gap: 0.1875rem;
  justify-content: center;
  padding-top: 0.125rem;
}

.cal-month__dot {
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: var(--rounded-full);
}
</style>
